import AgentPreferences, { ThemeMode } from 'models/agent_preferences';
import GatewayErrorSilentHandler from 'scripts/application/lib/gateway_error_silent_handler';
import i18n, { resolveSupportedLanguage, setLanguage } from 'scripts/i18n/i18n';
import { setTheme } from 'scripts/presentation/themes/theme_toggle';
import { writeAgentPreferencesCache } from 'scripts/adapters/stores/lib/legacy_agent_preferences';

// Loads agent preferences from the backend on agent subscribe. We don't apply
// broadcasts back into the store: setAgentPreferences updates the store
// optimistically, and the backend echo of that same write would otherwise race
// with subsequent optimistic updates (an older echo arriving after a newer write
// would overwrite the user's latest change).
export default class AgentPreferencesGatewayObserver {
  constructor(context) {
    this.context = context;
    this.errorHandler = new GatewayErrorSilentHandler(this.context);
  }

  onFetchSuccess(dto) {
    if (!dto || !dto.agentId) {
      return;
    }
    const preferences = AgentPreferences.fromJs(dto);
    this.context.stores.agentPreferences.set(preferences);
    writeAgentPreferencesCache(preferences);
    this.seedAnswerSearchPrefs(dto, preferences);
    this.applyUiLanguage(dto, preferences);
    // Only reapply the persisted theme when the backend actually carries a value.
    // A prefs record that predates themeMode (or a brand-new agent) omits the
    // field — applying the model default (light) would otherwise force every
    // existing dark-mode agent back to light on each subscribe/reconnect.
    if (Object.prototype.hasOwnProperty.call(dto, 'themeMode')) {
      setTheme(preferences.themeMode === ThemeMode.DARK ? 'dark' : 'light');
    }
  }

  // The live answerPanel store is seeded once at boot from the localStorage
  // cache, so on a brand-new device its search language/audiences would stay at
  // the defaults until a same-device reload repopulated the cache. Seed them
  // from the backend record here so they sync cross-device on first load. As
  // with the theme, only apply a field the dto actually carries — a prefs record
  // predating it would otherwise clobber the cached value with the model default.
  seedAnswerSearchPrefs(dto, preferences) {
    const answerPanel = this.context.stores.answerPanel.get();
    if (Object.prototype.hasOwnProperty.call(dto, 'answerSearchLanguage')) {
      answerPanel.setSearchLanguage(preferences.answerSearchLanguage);
    }
    if (Object.prototype.hasOwnProperty.call(dto, 'answerSearchAudiences')) {
      answerPanel.setSearchAudiences(preferences.answerSearchAudiences);
    }
    this.context.stores.answerPanel.set(answerPanel);
  }

  // Apply the persisted UI language on load so it syncs across devices. Only act
  // when the backend actually carries a value (a record predating the field, or a
  // new agent, omits it — see AgentPreferences omitempty), and skip when it
  // already matches the active language: changeLanguage remounts the app on
  // languageChanged, so a no-op reapply on every subscribe/reconnect would churn.
  // Compare what setLanguage would actually switch to (the clamped,
  // base-language form) rather than the raw stored value, so a region-suffixed or
  // unrecognized value doesn't read as a change and remount the app on every
  // subscribe.
  applyUiLanguage(dto, preferences) {
    if (!Object.prototype.hasOwnProperty.call(dto, 'uiLanguage') || !preferences.uiLanguage) {
      return;
    }
    const active = (i18n.resolvedLanguage || i18n.language || '').split('-')[0];
    const desired = resolveSupportedLanguage(preferences.uiLanguage);
    if (desired !== active) {
      setLanguage(desired);
    }
  }

  onFetchError(err) {
    this.errorHandler.handleCommonErrors(err);
  }

  onReplaceError(err) {
    this.errorHandler.handleCommonErrors(err);
  }
}
